// Admin dashboard — leaf sub-components extracted from AdminDashboard.jsx
// to keep the parent file manageable. Every component in this file is a
// pure presentational leaf: it reads its own data via a small prop
// contract (or a single scoped fetch, in the case of NotificationBadges)
// and never touches AdminDashboard's local state. No behavior was
// changed as part of the P3 refactor — this file is a straight
// structural move.

import React from "react";
import { format, isValid, parseISO } from "date-fns";
import {
  Calendar, CalendarDays, MessageCircle, CheckCircle2, AlertCircle,
  ArrowUpDown, X,
} from "lucide-react";
import api from "@/lib/api";
import { Calendar as CalendarUI } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { NOTIF_LABELS, SORT_GROUPS, SORT_LABELS, SALESMEN } from "./utils";

// ---------------------- Notification Badges --------------------------------
// WhatsApp delivery/pickup notification status list shown inside the
// Detail Drawer. Scoped GET — the endpoint returns only what the
// current admin group is allowed to see.
export const MOBILE_VERIFICATION_LABELS = {
  server_verified: { label: "Server verified", title: "MSG91 OTP confirmed by the server", tone: "bg-[#F0FDF4] text-[#15803D] border-[#BBF7D0]" },
  provider_unconfirmed: { label: "Pending confirmation", title: "OTP accepted by the widget; server confirmation unavailable", tone: "bg-[#FFFBEB] text-[#92400E] border-[#FDE68A]" },
  not_verified: { label: "Not verified", title: "Customer skipped OTP — call to confirm before dispatch", tone: "bg-[#FFFBEB] text-[#92400E] border-[#FDE68A]" },
  not_required: { label: "Not required", title: "Mobile verification was not required for this submission", tone: "bg-[#F5F2EC] text-muted2 border-line" },
};
const UNVERIFIED = { label: "Not recorded", title: "Submitted before verification tracking", tone: "bg-[#F5F2EC] text-muted2 border-line" };

export function MobileVerificationBadge({ status, testId, compact = false }) {
  const meta = MOBILE_VERIFICATION_LABELS[status] || UNVERIFIED;
  const Icon = status === "server_verified" ? CheckCircle2 : AlertCircle;
  return (
    <span
      data-testid={testId}
      data-status={MOBILE_VERIFICATION_LABELS[status] ? status : "not_recorded"}
      title={meta.title}
      className={`inline-flex items-center gap-1 rounded-full border font-medium whitespace-nowrap ${
        compact ? "text-[10px] px-1.5 py-px" : "text-[11px] px-2 py-0.5"
      } ${meta.tone}`}
    >
      <Icon size={compact ? 10 : 11} strokeWidth={1.9} />
      {meta.label}
    </span>
  );
}

export const NotificationBadges = ({ submissionId }) => {
  const [data, setData] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    if (!submissionId) return;
    api.get(`/admin/submissions/${submissionId}/notifications`)
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch(() => { if (!cancelled) setData({}); });
    return () => { cancelled = true; };
  }, [submissionId]);
  const rows = data ? Object.entries(data) : [];
  if (!data) return null;
  return (
    <div className="border border-line rounded-xl p-3 bg-ivory/40" data-testid="drawer-whatsapp-block">
      <div className="flex items-center gap-2 mb-2">
        <MessageCircle size={13} strokeWidth={1.7} className="text-maroon" />
        <p className="overline text-[10px]">WhatsApp</p>
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-muted2 italic" data-testid="drawer-whatsapp-empty">
          No notifications recorded yet.
        </p>
      ) : (
        <ul className="space-y-1.5" data-testid="drawer-whatsapp-list">
          {rows.map(([type, info]) => {
            const status = info?.status || "unknown";
            const isSent = status === "sent";
            const isFailed = status === "failed";
            const isSkipped = status === "skipped";
            const Icon = isSent ? CheckCircle2 : isFailed ? AlertCircle : MessageCircle;
            const tone = isSent
              ? "text-[#2D4A22]"
              : isFailed
              ? "text-[#8A2A2A]"
              : "text-muted2";
            const label = isSent
              ? "sent"
              : isFailed
              ? "failed"
              : isSkipped
              ? "queued (preview off)"
              : status;
            return (
              <li
                key={type}
                data-testid={`drawer-whatsapp-${type}`}
                className="flex items-center justify-between gap-2 text-xs"
              >
                <span className="inline-flex items-center gap-1.5 text-ink">
                  <Icon size={12} strokeWidth={1.8} className={tone} />
                  {NOTIF_LABELS[type] || type}
                </span>
                <span className={`text-[11px] ${tone}`} title={info?.error || ""}>
                  {label}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

/**
 * Attention indicators shown on every Active Submissions row.
 *   ✦ (Sparkle)  — the customer left a Special Instructions note.
 *   ✨ (Sparkles) — the customer requested Additional Paid Customisation.
 * Both mount only when their underlying data is truthy so an invoice
 * with neither shows no chrome at all. Tapping either indicator opens
 * the existing View More drawer — this is a SHORTCUT, not a new
 * details surface, so the drawer remains the source of truth.
 */
export function AttentionMarks({ row, onOpen, testPrefix = "attn" }) {
  const hasNote = Boolean((row?.special_instructions || "").trim());
  const hasCust = row?.additional_customisation === "yes";
  if (!hasNote && !hasCust) return null;
  const handle = (e) => { e.stopPropagation(); onOpen && onOpen(); };
  return (
    <span
      data-testid={`${testPrefix}-attention-${row.id}`}
      className="inline-flex items-center gap-1 ml-0.5"
    >
      {hasNote && (
        <button
          type="button"
          onClick={handle}
          data-testid={`${testPrefix}-attn-note-${row.id}`}
          title="Customer left a note — tap to view"
          aria-label="Customer note attached"
          className="inline-flex items-center justify-center w-5 h-5 rounded-full text-maroon hover:bg-maroon/[0.08] transition-colors text-[13px] leading-none"
        >
          <span aria-hidden="true">✦</span>
        </button>
      )}
      {hasCust && (
        <button
          type="button"
          onClick={handle}
          data-testid={`${testPrefix}-attn-cust-${row.id}`}
          title="Additional paid customisation — tap to view"
          aria-label="Additional customisation requested"
          className="inline-flex items-center justify-center w-5 h-5 rounded-full text-[#B79E75] hover:bg-[#FBF6E9] transition-colors text-[13px] leading-none"
        >
          <span aria-hidden="true">✨</span>
        </button>
      )}
    </span>
  );
}

// Compact label/value pair used in the Detail Drawer.
export const Info = ({ label, value, icon: Icon }) => (
  <div>
    <p className="overline text-[10px] mb-1">{label}</p>
    <p className="text-sm text-ink flex items-center gap-2">
      {Icon && <Icon size={14} strokeWidth={1.5} className="text-muted2" />}
      {value}
    </p>
  </div>
);

/**
 * Reusable filter-bar date picker built on shadcn Popover + Calendar.
 * - Renders a full-width, tap-friendly trigger button (matches luxury-input styling)
 * - Popover width is clamped to viewport so it never overflows on mobile
 * - Emits YYYY-MM-DD strings — identical contract to the previous <input type="date">
 */
export const DateFilterField = ({ value, onChange, placeholder = "Select date", testId }) => {
  const [open, setOpen] = React.useState(false);
  const parsed = React.useMemo(() => {
    if (!value) return null;
    const d = parseISO(value);
    return isValid(d) ? d : null;
  }, [value]);
  const pretty = parsed ? format(parsed, "d MMM yyyy") : "";
  return (
    <div className="relative w-full">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={testId}
            className={`luxury-input w-full text-left inline-flex items-center justify-between gap-2 !py-2.5 ${
              value ? "text-ink" : "text-muted2"
            } ${value ? "!pr-9" : ""}`}
          >
            <span className="inline-flex items-center gap-2 min-w-0">
              <Calendar size={14} strokeWidth={1.6} className="text-muted2 flex-shrink-0" />
              <span className="truncate text-sm">{pretty || placeholder}</span>
            </span>
            {!value && (
              <CalendarDays size={14} strokeWidth={1.6} className="text-muted2 flex-shrink-0" />
            )}
          </button>
        </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={6}
        collisionPadding={12}
        className="p-2 w-[min(20rem,calc(100vw-1.5rem))] max-w-[20rem]"
      >
        <CalendarUI
          mode="single"
          selected={parsed || undefined}
          onSelect={(d) => {
            if (!d) return;
            onChange(format(d, "yyyy-MM-dd"));
            setOpen(false);
          }}
          showOutsideDays={false}
          className="!p-0 w-full"
          classNames={{
            months: "flex flex-col w-full",
            month: "w-full space-y-3",
            caption: "flex justify-center pt-1 relative items-center px-2",
            caption_label: "font-serif text-sm text-ink",
            nav: "space-x-1 flex items-center",
            nav_button: "h-8 w-8 bg-transparent p-0 rounded-full inline-flex items-center justify-center border border-line text-muted2 hover:text-maroon hover:border-maroon/40 transition-colors",
            nav_button_previous: "absolute left-1",
            nav_button_next: "absolute right-1",
            table: "w-full border-collapse",
            head_row: "grid grid-cols-7",
            head_cell: "text-muted2 uppercase tracking-[0.14em] text-[10px] py-2 text-center",
            row: "grid grid-cols-7 mt-1",
            cell: "relative p-0 text-center text-sm aspect-square flex items-center justify-center",
            day: "h-8 w-8 sm:h-9 sm:w-9 p-0 font-normal rounded-full text-ink hover:bg-maroon/[0.08] hover:text-maroon transition-colors inline-flex items-center justify-center text-sm",
            day_selected: "!bg-maroon !text-ivory hover:!bg-maroon hover:!text-ivory",
            day_today: "ring-1 ring-maroon/40 text-maroon",
            day_disabled: "text-muted2/40 cursor-not-allowed hover:bg-transparent hover:text-muted2/40",
            day_outside: "text-muted2/40",
          }}
        />
        {value && (
          <div className="pt-1 mt-1 border-t border-line">
            <button
              type="button"
              onClick={() => { onChange(""); setOpen(false); }}
              className="w-full text-xs text-muted2 hover:text-maroon py-1.5 inline-flex items-center justify-center gap-1.5"
            >
              <X size={12} strokeWidth={1.8} /> Clear date
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
    {value && (
      <button
        type="button"
        data-testid={testId ? `${testId}-clear` : undefined}
        onClick={() => onChange("")}
        aria-label="Clear date"
        className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full text-muted2 hover:text-maroon hover:bg-maroon/[0.06] inline-flex items-center justify-center transition-colors"
      >
        <X size={13} strokeWidth={1.8} />
      </button>
    )}
    </div>
  );
};

/**
 * Salesman select cell — Group B (Bridal) invoice-row cell.
 * Read-only "Not Assigned" placeholder when the invoice has no
 * salesman yet; a plain <select> is used deliberately because it
 * matches the native OS picker on iOS / Android which staff prefer.
 */
export const SalesmanCell = ({ row, onChange }) => {
  const value = row.salesman || "";
  return (
    <select
      data-testid={`salesman-select-${row.id}`}
      value={value}
      onChange={(e) => onChange(row, e.target.value)}
      className={`text-xs px-2.5 py-1.5 rounded-full border cursor-pointer transition-colors appearance-none pr-6 bg-[right_0.4rem_center] bg-no-repeat ${
        value
          ? "border-maroon/25 bg-maroon/[0.06] text-maroon hover:bg-maroon/[0.10]"
          : "border-line bg-white text-muted2 hover:border-maroon/40 italic"
      }`}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 12 12' fill='none' stroke='currentColor' stroke-width='1.8'><path d='M3 5l3 3 3-3'/></svg>\")",
      }}
    >
      <option value="">Not Assigned</option>
      {SALESMEN.map((s) => <option key={s} value={s}>{s}</option>)}
    </select>
  );
};

/**
 * Internal-only delivery-date cell — never rendered on any customer page.
 * Uses shadcn Popover + Calendar so the same touch-friendly picker works
 * identically on desktop and iOS Safari / Android Chrome. Reuses the same
 * PATCH /admin/submissions/{id}/delivery-date backend endpoint.
 */
export const DeliveryDateCell = ({ row, onChange }) => {
  const raw = row.internal_delivery_date || "";
  const [open, setOpen] = React.useState(false);
  const pretty = React.useMemo(() => {
    if (!raw) return "";
    const d = parseISO(raw);
    return isValid(d) ? format(d, "d MMM yyyy") : raw;
  }, [raw]);
  return (
    <div className="inline-flex items-center gap-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={`delivery-date-btn-${row.id}`}
            className={`text-xs inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full border transition-colors ${
              raw
                ? "border-maroon/25 bg-maroon/[0.06] text-maroon hover:bg-maroon/[0.10]"
                : "border-line text-muted2 hover:border-maroon/40 hover:text-maroon italic"
            }`}
          >
            <CalendarDays size={12} strokeWidth={1.7} />
            {raw ? pretty : "Not Assigned"}
          </button>
        </PopoverTrigger>
        <PopoverContent
          data-testid={`delivery-date-popover-${row.id}`}
          align="start"
          side="bottom"
          sideOffset={6}
          className="p-2 w-[min(20rem,calc(100vw-1.5rem))] max-w-[20rem]"
        >
          <CalendarUI
            mode="single"
            selected={raw ? parseISO(raw) : undefined}
            onSelect={(d) => {
              if (!d) return;
              onChange(row, format(d, "yyyy-MM-dd"));
              setOpen(false);
            }}
            showOutsideDays={false}
            className="!p-0 w-full"
            classNames={{
              months: "flex flex-col w-full",
              month: "w-full space-y-3",
              caption: "flex justify-center pt-1 relative items-center px-2",
              caption_label: "font-serif text-sm text-ink",
              nav: "space-x-1 flex items-center",
              nav_button: "h-8 w-8 bg-transparent p-0 rounded-full inline-flex items-center justify-center border border-line text-muted2 hover:text-maroon hover:border-maroon/40 transition-colors",
              nav_button_previous: "absolute left-1",
              nav_button_next: "absolute right-1",
              table: "w-full border-collapse",
              head_row: "grid grid-cols-7",
              head_cell: "text-muted2 uppercase tracking-[0.14em] text-[10px] py-2 text-center",
              row: "grid grid-cols-7 mt-1",
              cell: "relative p-0 text-center text-sm aspect-square flex items-center justify-center",
              day: "h-8 w-8 sm:h-9 sm:w-9 p-0 font-normal rounded-full text-ink hover:bg-maroon/[0.08] hover:text-maroon transition-colors inline-flex items-center justify-center text-sm",
              day_selected: "!bg-maroon !text-ivory hover:!bg-maroon hover:!text-ivory",
              day_today: "ring-1 ring-maroon/40 text-maroon",
              day_disabled: "text-muted2/40 cursor-not-allowed hover:bg-transparent hover:text-muted2/40",
              day_outside: "text-muted2/40",
            }}
          />
          {raw && (
            <div className="pt-1 mt-1 border-t border-line">
              <button
                type="button"
                onClick={() => { onChange(row, ""); setOpen(false); }}
                data-testid={`delivery-date-clear-${row.id}`}
                className="w-full text-xs text-muted2 hover:text-maroon py-1.5 inline-flex items-center justify-center gap-1.5"
              >
                <X size={12} strokeWidth={1.8} /> Clear delivery date
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};

/**
 * Sort popover for the Active Submissions table. Purely presentational — reads
 * from and writes to the parent `sort` state; the actual sorting is performed
 * client-side in the AdminDashboard `sortedRows` useMemo, so filters and sort
 * compose freely without any server round-trip.
 */
export const SortMenu = ({ value, onChange }) => {
  const [open, setOpen] = React.useState(false);
  const activeLabel = value ? SORT_LABELS[value] : "Default";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="admin-sort-btn"
          className={`luxury-input w-full text-left inline-flex items-center justify-between gap-2 !py-2.5 ${
            value ? "text-ink" : "text-muted2"
          }`}
        >
          <span className="inline-flex items-center gap-2 min-w-0">
            <ArrowUpDown size={14} strokeWidth={1.6} className="text-muted2 flex-shrink-0" />
            <span className="truncate text-sm">
              {value ? (
                <>
                  <span className="text-muted2">Sort:</span>{" "}
                  <span data-testid="admin-sort-active-label" className="text-ink">{activeLabel}</span>
                </>
              ) : (
                <span data-testid="admin-sort-active-label">Sort: Default</span>
              )}
            </span>
          </span>
          <ArrowUpDown size={13} strokeWidth={1.6} className="text-muted2 flex-shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        data-testid="admin-sort-popover"
        align="start"
        side="bottom"
        sideOffset={6}
        collisionPadding={12}
        className="p-0 w-[min(22rem,calc(100vw-1.5rem))] max-w-[22rem] max-h-[min(70vh,32rem)] overflow-y-auto"
      >
        <div className="px-4 py-3 border-b border-line bg-ivory/70 sticky top-0 z-10">
          <p className="overline text-[10px]">Sort By</p>
        </div>
        <div className="py-2">
          {SORT_GROUPS.map((g) => (
            <div key={g.group} className="px-2 py-1.5">
              <p className="px-2 pt-1.5 pb-1 text-[10px] uppercase tracking-[0.2em] text-muted2 font-medium">
                {g.group}
              </p>
              <div className="flex flex-col">
                {g.options.map((o) => {
                  const active = value === o.value;
                  return (
                    <button
                      key={o.value}
                      type="button"
                      data-testid={`sort-option-${o.value}`}
                      onClick={() => { onChange(o.value); setOpen(false); }}
                      className={`text-left text-sm px-3 py-2.5 rounded-lg inline-flex items-center justify-between gap-2 transition-colors ${
                        active
                          ? "bg-maroon/[0.08] text-maroon"
                          : "text-ink hover:bg-ivory"
                      }`}
                    >
                      <span>{o.label}</span>
                      {active && (
                        <span aria-hidden className="h-2 w-2 rounded-full bg-maroon flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="px-3 py-2 border-t border-line sticky bottom-0 bg-white">
          <button
            type="button"
            data-testid="admin-sort-clear"
            onClick={() => { onChange(""); setOpen(false); }}
            disabled={!value}
            className="w-full text-xs uppercase tracking-[0.22em] text-muted2 hover:text-maroon py-2 inline-flex items-center justify-center gap-1.5 disabled:opacity-40 disabled:hover:text-muted2 disabled:cursor-not-allowed"
          >
            <X size={12} strokeWidth={1.8} /> Clear Sort
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
